import type { PostHog } from "posthog-js"
import { useState, useCallback, type ReactNode } from "react"

import { CookieConsentContext, type ConsentStatus } from "./useCookieConsent"

const CONSENT_COOKIE_NAME = "cookie_consent"
const CONSENT_REGION_COOKIE_NAME = "consent-region"
const CONSENT_EXPIRY_DAYS = 180 // 6 months
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match?.[2] ?? null
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  const secure = window.location.protocol === "https:" ? ";Secure" : ""
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict${secure}`
}

function deleteCookie(name: string) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
}

function deletePostHogCookies() {
  // Get all cookies and delete any that start with 'ph_'
  const cookies = document.cookie.split(";")
  for (const cookie of cookies) {
    const cookieName = cookie.split("=")[0]?.trim() ?? ""
    if (cookieName.startsWith("ph_")) {
      deleteCookie(cookieName)
    }
  }
}

function checkIfConsentRequired(): boolean {
  // Read the consent-region cookie set by Cloudflare middleware
  // Format: "country:region:requires" (e.g., "US:CA:true" or "JP:unknown:false")
  const regionCookie = getCookie(CONSENT_REGION_COOKIE_NAME)

  if (!regionCookie) {
    // No cookie means local development (no Cloudflare middleware)
    // Don't show banner in local dev
    return false
  }

  const parts = regionCookie.split(":")
  if (parts.length >= 3) {
    return parts[2] === "true"
  }

  // Malformed cookie, assume consent required for safety
  return true
}

function getInitialConsentState(): {
  consentStatus: ConsentStatus
  requiresConsent: boolean
  showBanner: boolean
} {
  const consentRequired = checkIfConsentRequired()
  const existingConsent = getCookie(CONSENT_COOKIE_NAME)

  if (!consentRequired) {
    return {
      consentStatus: "accepted",
      requiresConsent: false,
      showBanner: false,
    }
  }
  if (existingConsent === "accepted") {
    return {
      consentStatus: "accepted",
      requiresConsent: true,
      showBanner: false,
    }
  }
  if (existingConsent === "rejected") {
    return {
      consentStatus: "rejected",
      requiresConsent: true,
      showBanner: false,
    }
  }
  return {
    consentStatus: "undecided",
    requiresConsent: true,
    showBanner: true,
  }
}

if (import.meta.env.DEV && typeof window !== "undefined") {
  const setRegion = (country: string, region: string, requiresConsent: boolean) => {
    document.cookie = `${CONSENT_REGION_COOKIE_NAME}=${country}:${region}:${requiresConsent}; path=/`
    window.location.reload()
  }

  Object.assign(window, {
    testConsent: {
      setEU: () => setRegion("DE", "unknown", true),
      setCalifornia: () => setRegion("US", "CA", true),
      setUS: () => setRegion("US", "NY", false),
      setCustom: setRegion,
      clear: () => {
        deleteCookie(CONSENT_REGION_COOKIE_NAME)
        deleteCookie(CONSENT_COOKIE_NAME)
        window.location.reload()
      },
    },
  })

  console.log(
    "Cookie consent test helpers available:\n" +
      "  testConsent.setEU()        - Simulate EU user\n" +
      "  testConsent.setCalifornia() - Simulate California user\n" +
      "  testConsent.setUS()        - Simulate US (non-CA) user\n" +
      "  testConsent.setCustom(country, region, requiresConsent)\n" +
      "  testConsent.clear()        - Reset all consent cookies",
  )
}

interface CookieConsentProviderProps {
  children: ReactNode
}

/**
 * Synchronously resolve the PostHog persistence mode from consent cookies.
 * When consent is already granted (or not required for the visitor's region),
 * returns "localStorage+cookie" so the anonymous distinct_id survives
 * cross-app navigations on entire.io (website → /login). Falls back to
 * "memory" when consent is undecided so no cookies are written prematurely.
 */
export function getInitialPostHogPersistence(): "memory" | "localStorage+cookie" {
  const state = getInitialConsentState()
  return state.consentStatus === "accepted" ? "localStorage+cookie" : "memory"
}

/**
 * Initialize rejected visitors in PostHog's supported consent-transition mode.
 * This prevents PostHog from creating a session manager that would become stale
 * when cookieless tracking starts after mount.
 */
export function getInitialPostHogCookielessMode(): "on_reject" | undefined {
  return getInitialConsentState().consentStatus === "rejected" ? "on_reject" : undefined
}

type PostHogConsentClient = Pick<
  PostHog,
  "set_config" | "opt_in_capturing" | "opt_out_capturing" | "get_explicit_consent_status" | "reset"
> & {
  config: Pick<PostHog["config"], "cookieless_mode">
}

/**
 * Move PostHog between persistent and cookieless tracking through its consent
 * APIs. Setting `cookieless_mode: "always"` at runtime leaves PostHog's session
 * manager active, causing later captures to throw.
 */
export function syncPostHogConsent(posthog: PostHogConsentClient, status: ConsentStatus): void {
  if (status === "accepted") {
    posthog.set_config({ persistence: "localStorage+cookie" })
    if (posthog.get_explicit_consent_status() !== "granted") {
      posthog.opt_in_capturing()
    }
  } else if (status === "rejected") {
    const alreadyRejected =
      posthog.config.cookieless_mode === "on_reject" &&
      posthog.get_explicit_consent_status() === "denied"
    if (alreadyRejected) return

    posthog.set_config({ cookieless_mode: "on_reject" })
    posthog.reset(true)
    posthog.opt_out_capturing()
  }
}

/**
 * Synchronously resolve the current consent status from the same cookies,
 * for non-React consumers that initialize before hydration (Datadog RUM).
 */
export function getInitialConsentStatus(): ConsentStatus {
  return getInitialConsentState().consentStatus
}

/** Sync initial consent after PostHog has initialized with its real project token. */
export function syncInitialPostHogConsent(posthog: PostHogConsentClient): void {
  syncPostHogConsent(posthog, getInitialConsentStatus())
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [state, setState] = useState(getInitialConsentState)

  const { consentStatus, requiresConsent, showBanner } = state

  const acceptCookies = useCallback(() => {
    setCookie(CONSENT_COOKIE_NAME, "accepted", CONSENT_EXPIRY_DAYS)
    setState((prev) => ({
      ...prev,
      consentStatus: "accepted",
      showBanner: false,
    }))
  }, [])

  const rejectCookies = useCallback(() => {
    setCookie(CONSENT_COOKIE_NAME, "rejected", CONSENT_EXPIRY_DAYS)
    setState((prev) => ({
      ...prev,
      consentStatus: "rejected",
      showBanner: false,
    }))
    deletePostHogCookies()
  }, [])

  const openBanner = useCallback(() => {
    if (requiresConsent) {
      setState((prev) => ({ ...prev, showBanner: true }))
    }
  }, [requiresConsent])

  return (
    <CookieConsentContext.Provider
      value={{
        consentStatus,
        requiresConsent,
        showBanner,
        acceptCookies,
        rejectCookies,
        openBanner,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}
